/**
 * 项目管理中心 - 现代化设计
 * 提供优雅的项目列表展示和管理功能
 * 集成后端API，显示真实的项目列表和状态
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetIssuerProjectsQuery } from '../../store/api/issuerProjectApi';
import {
  useGetProjectWorkflowsQuery,
  useGetProjectManagementStatisticsQuery,
} from '../../store/api/projectManagementApi';
import ModernProjectCard from '../../components/project-management/ModernProjectCard';
import EnhancedPageHeader from '../../components/project-management/EnhancedPageHeader';
import {
  Row,
  Col,
  Tabs,
  Typography,
  Space,
  Button,
  Progress,
  Input,
  Select,
  FloatButton,
  Badge,
  Empty,
  message,
  Tooltip,
} from 'antd';
import {
  ProjectOutlined,
  FilterOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
} from '@ant-design/icons';

import StandardPageLayout from '../../components/layout/StandardPageLayout';
import {
  UnifiedDataTable,
  type UnifiedTableColumn,
  useUnifiedConfirm,
} from '../../components/unified';
import { emeraldColors } from '../../theme/emeraldTheme';
import { EMERALD_THEME } from '../../config/designTokens';
import type { Project } from '../../types/project-management';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

// Mock数据
const mockProjects: Project[] = [
  {
    id: 'proj_001',
    name: '华能清洁能源绿色债券',
    issuer_name: '华能清洁能源股份有限公司',
    issuer_id: 'issuer_001',
    stage: 'issuance_preparation',
    status: 'active',
    progress: 45,
    issuance_amount: 100000, // 10亿元 = 100000万元
    currency: 'CNY',
    term: 5,
    interest_rate: 3.5,
    green_project_type: '清洁能源',
    environmental_benefit: '预计年减碳量50万吨CO2当量',
    created_at: '2024-01-15',
    updated_at: '2024-03-20',
    expected_completion: '2024-12-31',
    project_manager: '李明',
    exchange_name: '上海证券交易所',
    service_providers: [],
    estimated_cost: 120000, // 预估成本12亿元
    risk_level: 'medium',
    tags: ['绿色债券', '清洁能源', '碳减排'],
  },
  {
    id: 'proj_002',
    name: '中广核新能源债券项目',
    issuer_name: '中国广核新能源控股有限公司',
    issuer_id: 'issuer_002',
    stage: 'listing_trading',
    status: 'completed',
    progress: 100,
    issuance_amount: 80000, // 8亿元 = 80000万元
    currency: 'CNY',
    term: 3,
    interest_rate: 3.2,
    green_project_type: '核电清洁能源',
    environmental_benefit: '核电清洁能源项目融资',
    created_at: '2024-02-01',
    updated_at: '2024-03-15',
    expected_completion: '2024-08-31',
    project_manager: '王芳',
    exchange_name: '深圳证券交易所',
    service_providers: [],
    estimated_cost: 96000, // 预估成本9.6亿元
    risk_level: 'low',
    tags: ['绿色债券', '核电', '清洁能源'],
  },
  {
    id: 'proj_003',
    name: '比亚迪绿色交通债券',
    issuer_name: '比亚迪股份有限公司',
    issuer_id: 'issuer_003',
    stage: 'detailed_assessment',
    status: 'pending',
    progress: 20,
    issuance_amount: 150000, // 15亿元 = 150000万元
    currency: 'CNY',
    term: 7,
    interest_rate: 3.8,
    green_project_type: '绿色交通',
    environmental_benefit: '新能源汽车及充电基础设施建设',
    created_at: '2024-03-01',
    updated_at: '2024-03-18',
    expected_completion: '2025-03-01',
    project_manager: '张强',
    exchange_name: '上海证券交易所',
    service_providers: [],
    estimated_cost: 180000, // 预估成本18亿元
    risk_level: 'medium',
    tags: ['绿色债券', '新能源汽车', '充电桩'],
  },
];

const IssuerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cards');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const confirm = useUnifiedConfirm();
  const { t } = useTranslation();
  // 发行人不能手动创建项目，项目由交易所审核问卷通过后自动创建

  const normalizeBondTerm = (bondTerm?: string | null) => {
    const rawTerm = (bondTerm || '').trim();
    if (!rawTerm) return t('project:projectDetail.values.unknownTerm');

    const normalized = rawTerm.toLowerCase();
    if (
      ['未知期限', 'unknown term', 'unknown', 'tbd', '待确定', 'n/a'].includes(
        normalized
      )
    ) {
      return t('project:projectDetail.values.unknownTerm');
    }

    const yearMatch =
      rawTerm.match(/^(\d+)\s*年$/) ||
      rawTerm.match(/^(\d+)\s*(years?|yrs?|y)$/i);
    if (yearMatch?.[1]) {
      return t('project:projectDetail.values.termYears', {
        count: Number(yearMatch[1]),
      });
    }

    return rawTerm;
  };

  const normalizeProjectType = (projectType?: unknown) => {
    const rawType = Array.isArray(projectType)
      ? String(projectType[0] || '').trim()
      : String(projectType || '').trim();

    if (!rawType) return t('project:projectDetail.values.unknownProjectType');

    const normalized = rawType.toLowerCase();
    if (
      ['未知类型', 'unknown type', 'unknown', 'tbd', '待确定', 'n/a'].includes(
        normalized
      )
    ) {
      return t('project:projectDetail.values.unknownProjectType');
    }

    return rawType;
  };

  const normalizeBondAmountDisplay = (bondAmountDisplay?: string | null) => {
    const rawAmount = (bondAmountDisplay || '').trim();
    if (!rawAmount || rawAmount === '0') {
      return t('project:common.tbd');
    }
    return rawAmount;
  };

  const getLocalizedCurrentStage = (stage?: string | null) => {
    const stageKeyMap: Record<string, string> = {
      questionnaire: 'questionnaire',
      material_preparation: 'materialPreparation',
      preliminary_assessment: 'preliminaryAssessment',
      detailed_assessment: 'detailedAssessment',
      due_diligence: 'dueDiligence',
      project_approval: 'projectApproval',
      issuance_preparation: 'issuancePreparation',
      rating_application: 'issuancePreparation',
      roadshow: 'issuance',
      green_assessment: 'greenAssessment',
      formal_issuance: 'formalIssuance',
      listing_trading: 'listingTrading',
      exchange_review: 'exchangeReview',
      compliance_check: 'complianceCheck',
      documentation: 'documentation',
      approval: 'approval',
      issuance: 'issuance',
      post_issuance: 'postIssuance',
      ongoing_management: 'postIssuance',
    };

    const stageKey = stageKeyMap[String(stage || '').toLowerCase()];
    if (!stageKey) {
      return t('project:projectDetail.values.unknownStage');
    }
    return t(`project:common.stages.${stageKey}`);
  };

  const getLocalizedProjectStatus = (status?: string | null) => {
    const statusKeyMap: Record<string, string> = {
      active: 'project:common.projectStatus.active',
      in_progress: 'project:common.projectStatus.active',
      pending: 'project:common.projectStatus.pending',
      completed: 'project:common.projectStatus.completed',
      suspended: 'project:common.projectStatus.suspended',
      paused: 'project:common.projectStatus.paused',
      cancelled: 'project:common.projectStatus.cancelled',
    };

    const statusKey = statusKeyMap[String(status || '').toLowerCase()];
    if (!statusKey) {
      return t('project:projectDetail.values.unknownStatus');
    }
    return t(statusKey);
  };

  // 使用真实的项目工作流API
  const {
    data: projectsData,
    isLoading,
    error,
    refetch,
  } = useGetProjectWorkflowsQuery({
    page: 1,
    limit: 50, // 获取更多项目用于展示
    search: searchText || undefined,
    status: statusFilter !== 'all' ? [statusFilter] : undefined,
  });

  // 获取项目管理统计数据
  const { data: statisticsData, isLoading: isLoadingStatistics } =
    useGetProjectManagementStatisticsQuery();

  const projects = projectsData?.data.projects || [];

  // 过滤项目数据（API已处理大部分筛选，这里做本地补充过滤）
  const filteredProjects = projects.filter(project => {
    // 类型过滤（基于绿色项目类型）
    if (
      typeFilter !== 'all' &&
      !project.green_project_type.includes(typeFilter)
    ) {
      return false;
    }

    return true;
  });

  // 页面配置
  const breadcrumbs = [
    { label: t('project:dashboard.breadcrumb.home'), path: '/' },
    { label: t('project:dashboard.breadcrumb.dashboard') },
  ];

  // 统计数据 - 使用真实统计API数据
  const statistics = [
    {
      value: statisticsData?.data?.total_projects || 0,
      label: t('project:dashboard.stats.totalProjects'),
      color: '#fff',
    },
    {
      value: statisticsData?.data?.active_projects || 0,
      label: t('project:dashboard.stats.activeProjects'),
      color: '#fff',
    },
    {
      value: statisticsData?.data?.completed_projects || 0,
      label: t('project:dashboard.stats.completedProjects'),
      color: '#fff',
    },
  ];

  // 发行人不能手动创建项目（项目由交易所审核问卷通过后自动创建）

  // 处理加载状态和错误
  if (isLoading) {
    return (
      <StandardPageLayout
        title={t('project:dashboard.title')}
        subtitle={t('project:dashboard.subtitle')}
        breadcrumbs={[
          { label: t('project:dashboard.breadcrumb.home'), path: '/' },
          { label: t('project:dashboard.title') },
        ]}
        statistics={[
          {
            value: '-',
            label: t('project:dashboard.stats.totalProjects'),
            color: '#262626',
          },
          {
            value: '-',
            label: t('project:dashboard.stats.activeProjects'),
            color: '#10b981',
          },
          {
            value: '-',
            label: t('project:dashboard.stats.completedProjects'),
            color: '#52c41a',
          },
          {
            value: '-',
            label: t('project:dashboard.stats.avgProgress'),
            color: '#1890ff',
          },
        ]}
        actions={[]}
        icon={<ProjectOutlined style={{ fontSize: '24px', color: 'white' }} />}
        tags={[
          '🌱 ' + t('project:dashboard.tags.greenFinance'),
          '📊 ' + t('project:dashboard.tags.smartManagement'),
          '🚀 ' + t('project:dashboard.tags.efficientCollaboration'),
        ]}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{ fontSize: '16px', color: '#666', marginBottom: '16px' }}
            >
              {t('project:message.loadingData')}
            </div>
            <div>{t('project:message.loading')}</div>
          </div>
        </div>
      </StandardPageLayout>
    );
  }

  if (error) {
    return (
      <StandardPageLayout
        title={t('project:dashboard.title')}
        subtitle={t('project:dashboard.subtitle')}
        breadcrumbs={[
          { label: t('project:dashboard.breadcrumb.home'), path: '/' },
          { label: t('project:dashboard.title') },
        ]}
        statistics={[
          {
            value: '-',
            label: t('project:dashboard.stats.totalProjects'),
            color: '#262626',
          },
          {
            value: '-',
            label: t('project:dashboard.stats.activeProjects'),
            color: '#10b981',
          },
          {
            value: '-',
            label: t('project:dashboard.stats.completedProjects'),
            color: '#52c41a',
          },
          {
            value: '-',
            label: t('project:dashboard.stats.avgProgress'),
            color: '#1890ff',
          },
        ]}
        actions={[]}
        icon={<ProjectOutlined style={{ fontSize: '24px', color: 'white' }} />}
        tags={[
          '🌱 ' + t('project:dashboard.tags.greenFinance'),
          '📊 ' + t('project:dashboard.tags.smartManagement'),
          '🚀 ' + t('project:dashboard.tags.efficientCollaboration'),
        ]}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '16px',
                color: '#ff4d4f',
                marginBottom: '16px',
              }}
            >
              {t('project:message.loadFailed')}
            </div>
            <Button type="primary" onClick={() => refetch()}>
              {t('project:message.reload')}
            </Button>
          </div>
        </div>
      </StandardPageLayout>
    );
  }

  // 处理项目操作
  const handleViewProject = (project: any) => {
    navigate(`/admin/project-management/projects/${project.project_code}`);
  };

  const handleEditProject = (project: any) => {
    setSelectedProject(project);
    // TODO: 实现编辑功能
    message.warning(t('project:message.featureComingSoon'));
  };

  const handleDeleteProject = (project: any) => {
    confirm.show({
      title: t('project:dashboard.confirmDelete'),
      content: (
        <div>
          <p>
            {t('project:dashboard.confirmDeleteContent', {
              name: project.project_name,
            })}
          </p>
          <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
            ⚠️ {t('project:dashboard.cannotUndo')}
          </p>
        </div>
      ),
      okText: t('project:dashboard.confirm'),
      type: 'error',
      onConfirm: () => {
        message.success(t('project:message.deleteSuccess'));
      },
    });
  };

  // 项目表格列配置 - 适配新的数据结构
  const projectColumns: UnifiedTableColumn<any>[] = [
    {
      title: t('project:dashboard.column.projectName'),
      dataIndex: 'project_name',
      key: 'project_name',
      width: 280,
      ellipsis: { showTitle: false },
      render: (text: string, record: any) => (
        <Tooltip
          title={t('project:dashboard.column.clickToView')}
          placement="topLeft"
        >
          <div
            onClick={() => handleViewProject(record)}
            style={{ cursor: 'pointer' }}
          >
            <div
              style={{
                fontWeight: '600',
                color: '#10b981',
                marginBottom: '4px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#059669')}
              onMouseLeave={e => (e.currentTarget.style.color = '#10b981')}
            >
              {text}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              {record.issuer_name}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('project:dashboard.column.bondInfo'),
      key: 'bondInfo',
      width: 200,
      render: (text: any, record: any) => (
        <div>
          <div
            style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}
          >
            {normalizeBondAmountDisplay(record.bond_amount_display)} •{' '}
            {normalizeBondTerm(record.bond_term)}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {t('project:common.projectType') + ': '}
            {normalizeProjectType(record.green_project_type)}
          </div>
        </div>
      ),
    },
    {
      title: t('project:dashboard.column.status'),
      dataIndex: 'overall_status',
      key: 'overall_status',
      width: 120,
      render: (status: string, record: any) => {
        const normalizedStatus = String(status || '').toLowerCase();
        const statusConfig: Record<
          string,
          { color: 'processing' | 'success' | 'warning' | 'default' }
        > = {
          active: { color: 'processing' },
          in_progress: { color: 'processing' },
          pending: { color: 'warning' },
          completed: { color: 'success' },
          suspended: { color: 'warning' },
          paused: { color: 'warning' },
          cancelled: { color: 'default' },
        };
        const config = statusConfig[normalizedStatus] || statusConfig.active;
        return (
          <div>
            <Badge
              status={config.color as any}
              text={getLocalizedProjectStatus(normalizedStatus)}
            />
            <div
              style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}
            >
              {getLocalizedCurrentStage(record.current_stage)}
            </div>
          </div>
        );
      },
    },
    {
      title: t('project:dashboard.column.progress'),
      dataIndex: 'progress_percentage',
      key: 'progress_percentage',
      width: 150,
      render: (progress: number) => (
        <Progress
          percent={progress}
          size="small"
          strokeColor="#10b981"
          style={{ minWidth: '100px' }}
        />
      ),
    },
    {
      title: t('project:dashboard.column.createTime'),
      dataIndex: 'created_at_display',
      key: 'created_at_display',
      width: 140,
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontSize: '13px', color: '#1f2937' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {record.project_code}
          </div>
        </div>
      ),
    },
    {
      title: t('project:dashboard.column.actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (text: any, record: any) => (
        <Space size="middle">
          <Tooltip title={t('project:common.viewDetails')}>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => handleViewProject(record)}
              style={{
                backgroundColor: '#10b981',
                borderColor: '#10b981',
                borderRadius: '6px',
                padding: '4px 12px',
                height: '32px',
              }}
            >
              {t('project:common.view')}
            </Button>
          </Tooltip>
          {/* Bug #20: 暂时隐藏未实现的编辑和删除功能 */}
          {/* <Tooltip title="编辑项目">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditProject(record)}
              style={{
                color: '#1890ff',
                borderColor: '#1890ff',
              }}
            />
          </Tooltip>
          <Tooltip title="删除项目">
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteProject(record)}
              style={{
                color: '#ff4d4f',
                borderColor: '#ff4d4f',
              }}
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  // 渲染快捷筛选和搜索
  const renderQuickFilters = () => (
    <Row gutter={16} style={{ marginBottom: '16px' }}>
      <Col xs={24} sm={12} md={8}>
        <Search
          placeholder={t('project:dashboard.search.placeholder')}
          allowClear
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ width: '100%' }}
        />
      </Col>
      <Col xs={12} sm={6} md={4}>
        <Select
          placeholder={t('project:dashboard.search.statusPlaceholder')}
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: '100%' }}
        >
          <Option value="all">{t('project:dashboard.search.allStatus')}</Option>
          <Option value="active">{t('project:dashboard.filter.active')}</Option>
          <Option value="completed">
            {t('project:dashboard.filter.completed')}
          </Option>
          <Option value="pending">
            {t('project:dashboard.filter.pending')}
          </Option>
          <Option value="suspended">
            {t('project:dashboard.filter.suspended')}
          </Option>
        </Select>
      </Col>
      <Col xs={12} sm={6} md={4}>
        <Select
          placeholder={t('project:dashboard.search.typePlaceholder')}
          value={typeFilter}
          onChange={setTypeFilter}
          style={{ width: '100%' }}
        >
          <Option value="all">{t('project:dashboard.search.allTypes')}</Option>
          <Option value="绿色债券">
            {t('project:dashboard.type.greenBond')}
          </Option>
          <Option value="可持续债券">
            {t('project:dashboard.type.sustainabilityBond')}
          </Option>
          <Option value="社会责任债券">
            {t('project:dashboard.type.socialBond')}
          </Option>
        </Select>
      </Col>
      <Col xs={24} sm={24} md={8} style={{ textAlign: 'right' }}>
        <Space>
          <Text style={{ fontSize: '16px', color: '#6b7280' }}>
            {t('project:dashboard.totalCount', { count: projects.length })}
          </Text>
          <Button icon={<FilterOutlined />} type="text">
            {t('project:dashboard.search.advancedFilter')}
          </Button>
        </Space>
      </Col>
    </Row>
  );

  // 渲染现代化项目卡片视图
  const renderProjectCards = () => (
    <Row gutter={[24, 24]}>
      {filteredProjects.map(project => (
        <Col xs={24} sm={12} lg={8} xl={6} key={project.project_code}>
          <ModernProjectCard
            project={project}
            onView={handleViewProject}
            onEdit={handleEditProject}
            onDelete={handleDeleteProject}
          />
        </Col>
      ))}
    </Row>
  );

  // 计算统计数据 - 数据加载期间使用占位符
  const enhancedStatistics = {
    totalProjects: isLoading ? '-' : filteredProjects.length,
    activeProjects: isLoading
      ? '-'
      : filteredProjects.filter(p => p.overall_status === 'active').length,
    completedProjects: isLoading
      ? '-'
      : filteredProjects.filter(p => p.overall_status === 'completed').length,
    avgProgress: isLoading
      ? '-'
      : Math.round(
          filteredProjects.reduce(
            (sum, p) => sum + (p.progress_percentage || 0),
            0
          ) / (filteredProjects.length || 1)
        ) + '%',
    monthlyGrowth: 12.5,
    highPriorityCount: isLoading
      ? '-'
      : filteredProjects.filter(p => (p.ai_score || 0) >= 85).length,
  };

  return (
    <StandardPageLayout
      title={t('project:dashboard.title')}
      subtitle={t('project:dashboard.subtitle')}
      breadcrumbs={[
        { label: t('project:dashboard.breadcrumb.home'), path: '/' },
        { label: t('project:dashboard.title') },
      ]}
      statistics={[
        {
          value: enhancedStatistics.totalProjects,
          label: t('project:dashboard.stats.totalProjects'),
          color: EMERALD_THEME.colors.statistics.total,
        },
        {
          value: enhancedStatistics.activeProjects,
          label: t('project:dashboard.stats.activeProjects'),
          color: EMERALD_THEME.colors.statistics.active,
        },
        {
          value: enhancedStatistics.completedProjects,
          label: t('project:dashboard.stats.completedProjects'),
          color: EMERALD_THEME.colors.statistics.completed,
        },
        {
          value: enhancedStatistics.avgProgress,
          label: t('project:dashboard.stats.avgProgress'),
          color: EMERALD_THEME.colors.statistics.pending,
        },
      ]}
      actions={[]}
      icon={<ProjectOutlined style={{ fontSize: '24px', color: 'white' }} />}
      tags={[
        '🌱 ' + t('project:dashboard.tags.greenFinance'),
        '📊 ' + t('project:dashboard.tags.smartManagement'),
        '🚀 ' + t('project:dashboard.tags.efficientCollaboration'),
      ]}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          marginTop: '0',
        }}
      >
        {/* 快捷筛选和搜索 */}
        {renderQuickFilters()}

        {/* 主要内容区 */}
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          <TabPane tab={t('project:dashboard.cardView')} key="cards">
            {filteredProjects.length > 0 ? (
              renderProjectCards()
            ) : (
              <Empty
                description={t('project:dashboard.emptyDescription')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </TabPane>

          <TabPane tab={t('project:dashboard.listView')} key="table">
            <UnifiedDataTable
              columns={projectColumns}
              dataSource={filteredProjects}
              rowKey="id"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('common:message.noData')}
                  />
                ),
              }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filteredProjects.length,
                onChange: page => setCurrentPage(page),
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  t('project:dashboard.paginationInfo', {
                    start: range[0],
                    end: range[1],
                    total,
                  }),
              }}
            />
          </TabPane>
        </Tabs>

        {/* 悬浮操作按钮 - Bug #20/#48: 暂时隐藏未实现的数据分析功能 */}
        {/* <FloatButton.Group
          trigger="hover"
          type="primary"
          style={{ right: 24 }}
          icon={<BarChartOutlined />}
        >
          <FloatButton
            icon={<BarChartOutlined />}
            tooltip="数据分析"
            onClick={() => message.info('数据分析功能开发中')}
          />
        </FloatButton.Group> */}
      </div>
    </StandardPageLayout>
  );
};

export default IssuerDashboard;
